import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IImageAssetService, ImageAsset } from '@advinsoftwares/ngx-rich-text-editor';
import { DocumentMasterService } from 'src/app/Common/DocumentMasterService/document-master.service';
import { DocumentMasterDTO } from 'src/app/Common/DocumentMasterService/documentmaster.interface';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { environment } from 'src/environments/environment';

/**
 * Wires the editor's Image Manager to AdvERP's real DocumentMaster upload
 * endpoint instead of the library's local-IndexedDB default. This posts
 * the same File/TenantId/TenantBranchId/SoftwareId fields the old
 * CuteEditor wrapper's customFileUploadHandler used, against the same
 * DocumentMaster_Upload endpoint — so uploaded images land in the exact
 * same place on the server as before.
 *
 * list() intentionally returns an empty array. DocumentMaster_Upload has
 * no "list previously uploaded images for this tenant" query today, and
 * the old CuteEditor integration never offered a reusable image library
 * either — every insert there was a fresh upload, no browse-and-reuse
 * step. So an empty list is parity with existing behavior, not a
 * regression. If a real reusable media library is ever wanted, that
 * needs a new backend list endpoint (e.g. GET DocumentMaster by
 * TenantId/TenantBranchId), not something to fake client-side here.
 *
 * delete() is a no-op for the same reason: there's no delete-by-id
 * endpoint wired up for these uploads yet. Removing an image from the
 * Image Manager's grid only removes it from that in-session list; the
 * uploaded file itself remains on the server, exactly as it did under
 * the old integration (which had no delete concept at all).
 */
@Injectable()
export class AdvErpImageAssetService implements IImageAssetService {
  private readonly documentMasterService = inject(DocumentMasterService);
  private readonly userLoginService = inject(UserLoginService);

  list(): Observable<ImageAsset[]> {
    return of([]);
  }

  upload(file: File): Observable<ImageAsset> {
    const user = this.userLoginService.User();
    const formData = new FormData();
    formData.append('File', file, file.name);
    formData.append('TenantId', user.companyId.toString());
    formData.append('TenantBranchId', user.loggedInBranchId.toString());
    formData.append('SoftwareId', environment.SoftwareId);

    return this.documentMasterService
      .DocumentMaster_Upload(formData)
      .pipe(map((response) => this.toImageAsset(file, response)));
  }

  delete(): Observable<void> {
    return of(void 0);
  }

  private toImageAsset(file: File, response: DocumentMasterDTO): ImageAsset {
    return {
      id: response.documentId ? response.documentId.toString() : crypto.randomUUID(),
      fileName: response.fileName || file.name,
      url: response.url || '',
      sizeBytes: file.size,
      contentType: file.type || 'application/octet-stream',
      createdAtUtc: response.createdOn
        ? new Date(response.createdOn).toISOString()
        : new Date().toISOString(),
    };
  }
}
